import { Loader2, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import {
  DrivingLicenceScreen,
  PassportScreen,
  VoterIdScreen,
} from '@/features/workflows/components/simple/preview-screens';
import { toast } from '@/lib/notify';
import { useVerifyStep } from '../../api/useVerifyStep';
import type {
  SessionDetailDto,
  SessionStepApiBindingDto,
  SessionStepDto,
} from '../../schemas/sessionSchemas';
import { extractReason } from '../pan/pan.utils';
import { isSessionExpiredError, SessionExpiredScreen } from '../SessionExpiredScreen';
import {
  CHECK_FAILED_TOAST,
  RETRY_EXHAUSTED_NOTE,
  tryAnotherMethodNote,
} from '../stepFallbackNotes';
import { IdVerificationDevSimulator } from './IdVerificationDevSimulator';
import {
  docTypeForBinding,
  ID_DOC_LABEL,
  type IdDocType,
  isValidDate,
  isValidDl,
  isValidEpic,
  isValidPassportNo,
} from './id.utils';

// Runs the combined ID Verification step. Only the PRIMARY (highest-priority)
// enabled document is shown — the applicant does NOT pick between documents. The
// other enabled documents are priority fallbacks: on a technical provider failure
// the runner advances to the next document's form (visible fallback note), exactly
// like the Bank / GST / PAN runners. Only the active document's API is called.
export function IdVerificationStepRunner({
  session,
  step,
}: {
  session: SessionDetailDto;
  step: SessionStepDto;
}) {
  const { mutate, isPending } = useVerifyStep(session.id);

  // The enabled document methods (priority order), each mapped to its doc type.
  const docs = useMemo(() => {
    return step.apiBindings
      .slice()
      .sort((a, b) => a.priority - b.priority)
      .map((b) => ({ binding: b, doc: docTypeForBinding(b.operationId) }))
      .filter((d): d is { binding: SessionStepApiBindingDto; doc: IdDocType } => d.doc !== null);
  }, [step.apiBindings]);

  const [activeIdx, setActiveIdx] = useState(0);
  const [voter, setVoter] = useState({ epicNo: '' });
  const [passport, setPassport] = useState({
    name: '',
    passportNo: '',
    fileNo: '',
    dob: '',
    doi: '',
  });
  const [dl, setDl] = useState({ dlNo: '', dob: '' });
  const [fallbackNote, setFallbackNote] = useState<string | null>(null);
  const [expired, setExpired] = useState(false);

  const active = docs[activeIdx] ?? docs[0];

  if (expired || session.isExpired) {
    return <SessionExpiredScreen />;
  }

  if (step.status === 'Failed') {
    return <FailureScreen reason={extractReason(step.resultJson)} />;
  }

  if (docs.length === 0 || !active) {
    return <p className="text-sm text-[#666666]">No document methods are enabled for this step.</p>;
  }

  // Captured after the guard so the closures below see non-undefined values
  // (TS control-flow narrowing doesn't reach into nested functions).
  const activeDoc = active.doc;
  const activeBindingId = active.binding.catalogueEntryId;

  function inputFor(doc: IdDocType): { input: Record<string, unknown>; ready: boolean } {
    switch (doc) {
      case 'voter_id':
        return {
          input: { epicNo: voter.epicNo.trim().toUpperCase() },
          ready: isValidEpic(voter.epicNo),
        };
      case 'passport':
        return {
          input: {
            name: passport.name.trim(),
            passportNo: passport.passportNo.trim().toUpperCase(),
            fileNo: passport.fileNo.trim(),
            dob: passport.dob.trim(),
            doi: passport.doi.trim(),
          },
          ready:
            passport.name.trim().length > 0 &&
            isValidPassportNo(passport.passportNo) &&
            passport.fileNo.trim().length > 0 &&
            isValidDate(passport.dob) &&
            isValidDate(passport.doi),
        };
      case 'driving_license':
        return {
          input: { dlNo: dl.dlNo.trim().toUpperCase(), dob: dl.dob.trim() },
          ready: isValidDl(dl.dlNo) && isValidDate(dl.dob),
        };
    }
  }

  const { input, ready } = inputFor(activeDoc);

  function submit(payload: Record<string, unknown>) {
    mutate(
      { stepId: step.id, input: payload, catalogueEntryId: activeBindingId || undefined },
      {
        onSuccess: (data) => {
          const updated = data.steps.find((s) => s.id === step.id);
          if (!updated) return;
          if (['Passed', 'Review', 'Failed'].includes(updated.status)) return;
          // status Pending → technical provider failure → advance to the next
          // enabled document (visible priority fallback), same as Bank / PAN.
          const next = docs[activeIdx + 1];
          if (next) {
            setFallbackNote(tryAnotherMethodNote(`your ${ID_DOC_LABEL[next.doc]}`));
            setActiveIdx(activeIdx + 1);
          } else {
            toast.error(CHECK_FAILED_TOAST);
            setFallbackNote(RETRY_EXHAUSTED_NOTE);
            setActiveIdx(0);
          }
        },
        onError: (error) => {
          if (isSessionExpiredError(error)) {
            setExpired(true);
            return;
          }
          toast.error('Could not reach the server. Please try again.');
        },
      },
    );
  }

  // DEV: one-click scenario for the SELECTED document.
  function runTestCase(testCase: string) {
    submit({ ...input, test_case: testCase });
  }

  return (
    <div className="flex flex-1 flex-col gap-4">
      {import.meta.env.DEV && (
        <IdVerificationDevSimulator doc={activeDoc} onRun={runTestCase} isPending={isPending} />
      )}

      {fallbackNote && (
        <div className="rounded-lg border border-[#FDE68A] bg-[#FFFBEB] px-3 py-2 text-xs text-[#92400E]">
          {fallbackNote}
        </div>
      )}

      {activeDoc === 'voter_id' && (
        <VoterIdScreen
          values={voter}
          onChange={(p) => setVoter((v) => ({ ...v, ...p }))}
          epicError={
            voter.epicNo.length > 0 && !isValidEpic(voter.epicNo)
              ? 'Enter a valid EPIC (e.g. ABC1234567).'
              : undefined
          }
        />
      )}
      {activeDoc === 'passport' && (
        // Errors were never passed here, unlike Voter ID — so an applicant who typed a
        // malformed passport number or an incomplete date just found Continue greyed
        // out with nothing telling them why. Each message appears only once the field
        // has been touched, so a blank form is not pre-scolded.
        <PassportScreen
          values={passport}
          onChange={(p) => setPassport((v) => ({ ...v, ...p }))}
          errors={{
            passportNo:
              passport.passportNo.length > 0 && !isValidPassportNo(passport.passportNo)
                ? 'Enter a valid passport number (e.g. S3733862).'
                : undefined,
            dob:
              passport.dob.length > 0 && !isValidDate(passport.dob)
                ? 'Enter the date as DD/MM/YYYY.'
                : undefined,
            doi:
              passport.doi.length > 0 && !isValidDate(passport.doi)
                ? 'Enter the date as DD/MM/YYYY.'
                : undefined,
          }}
        />
      )}
      {activeDoc === 'driving_license' && (
        <DrivingLicenceScreen
          values={dl}
          onChange={(p) => setDl((v) => ({ ...v, ...p }))}
          errors={{
            dlNo:
              dl.dlNo.length > 0 && !isValidDl(dl.dlNo)
                ? 'A licence number is 10–16 characters.'
                : undefined,
            dob:
              dl.dob.length > 0 && !isValidDate(dl.dob)
                ? 'Enter the date as DD/MM/YYYY.'
                : undefined,
          }}
        />
      )}

      <button
        type="button"
        onClick={() => ready && submit(input)}
        disabled={isPending || !ready}
        style={{
          backgroundColor: 'var(--brand-btn-fill, var(--color-primary))',
          color: 'var(--brand-btn-text, #ffffff)',
          borderColor: 'var(--brand-btn-stroke, var(--color-primary))',
          borderRadius: 'var(--brand-btn-radius, 0.75rem)',
        }}
        className="mt-auto flex w-full items-center justify-center gap-2 border py-3 text-[15px] font-semibold transition-opacity disabled:cursor-not-allowed disabled:opacity-40"
      >
        {isPending && <Loader2 size={15} className="animate-spin" />}
        Continue
      </button>
    </div>
  );
}

function FailureScreen({ reason }: { reason?: string | null }) {
  return (
    <div className="flex flex-1 flex-col items-center justify-center px-4 text-center">
      <div className="mb-6 flex h-[110px] w-[110px] items-center justify-center rounded-full bg-[#DC2626]">
        <X size={60} strokeWidth={3} color="white" />
      </div>
      <h2 className="mb-2 text-[length:var(--text-20)] font-semibold leading-tight text-[#DC2626]">
        ID verification failed
      </h2>
      <p className="text-[length:var(--text-14)] text-[#666666]">
        {reason ?? 'Your document could not be verified. This session has been rejected.'}
      </p>
    </div>
  );
}
